export const REVIEW_AI_REVIEW_TYPES = [
    'SAFETY',
    'DEFECT',
    'PRICING',
    'MISUSE',
    'SERVICE',
    'TRUST',
    'INQUIRY',
    'OPINION',
    'NOISE',
] as const;

export type ReviewAiReviewType = (typeof REVIEW_AI_REVIEW_TYPES)[number];
export type ReviewAiReviewTypeLabelLocale = 'ru' | 'en' | 'uz' | 'uzCyrl';

export const REVIEW_AI_ERROR_CODES = {
    aiRequestRejected: 'ai_analysis.application.ai_request_rejected',
} as const;

export type ReviewAiErrorCode = (typeof REVIEW_AI_ERROR_CODES)[keyof typeof REVIEW_AI_ERROR_CODES];

export type ReviewAiReviewTypeOption = {
    value: string;
    name: string;
};

export const REVIEW_AI_REVIEW_TYPE_LABELS: Record<ReviewAiReviewTypeLabelLocale, Record<ReviewAiReviewType, string>> = {
    ru: {
        SAFETY: 'Безопасность',
        DEFECT: 'Брак',
        PRICING: 'Ценность',
        MISUSE: 'Ожидания',
        SERVICE: 'Сервис',
        TRUST: 'Доверие',
        INQUIRY: 'Помощь',
        OPINION: 'Мнение',
        NOISE: 'Неопределен',
    },
    en: {
        SAFETY: 'Safety',
        DEFECT: 'Defect',
        PRICING: 'Value',
        MISUSE: 'Expectations',
        SERVICE: 'Service',
        TRUST: 'Trust',
        INQUIRY: 'Help',
        OPINION: 'Opinion',
        NOISE: 'Unclear',
    },
    uz: {
        SAFETY: 'Xavfsizlik',
        DEFECT: 'Nuqson',
        PRICING: 'Qiymat',
        MISUSE: 'Kutilmalar',
        SERVICE: 'Servis',
        TRUST: 'Ishonch',
        INQUIRY: 'Yordam',
        OPINION: 'Fikr',
        NOISE: 'Noaniq',
    },
    uzCyrl: {
        SAFETY: 'Хавфсизлик',
        DEFECT: 'Нуқсон',
        PRICING: 'Қиймат',
        MISUSE: 'Кутилмалар',
        SERVICE: 'Сервис',
        TRUST: 'Ишонч',
        INQUIRY: 'Ёрдам',
        OPINION: 'Фикр',
        NOISE: 'Ноаниқ',
    },
};

const REVIEW_AI_REVIEW_TYPE_SET = new Set<string>(REVIEW_AI_REVIEW_TYPES);

export const normalizeReviewAiReviewType = (value: unknown): ReviewAiReviewType | null => {
    if (typeof value !== 'string') {
        return null;
    }

    const normalizedValue = value.trim().toUpperCase();
    return REVIEW_AI_REVIEW_TYPE_SET.has(normalizedValue) ? (normalizedValue as ReviewAiReviewType) : null;
};

export const normalizeReviewAiReviewTypes = (value: unknown): ReviewAiReviewType[] => {
    if (!Array.isArray(value)) {
        return [];
    }

    const normalizedTypes: ReviewAiReviewType[] = [];
    for (const item of value) {
        const reviewType = normalizeReviewAiReviewType(item);
        if (reviewType && !normalizedTypes.includes(reviewType)) {
            normalizedTypes.push(reviewType);
        }
    }

    return normalizedTypes;
};

export const getReviewAiReviewTypeLabel = (
    reviewType: ReviewAiReviewType,
    locale: ReviewAiReviewTypeLabelLocale = 'ru',
): string => REVIEW_AI_REVIEW_TYPE_LABELS[locale]?.[reviewType] ?? REVIEW_AI_REVIEW_TYPE_LABELS.ru[reviewType];

export const resolveReviewAiReviewTypeLabel = (
    value: unknown,
    locale: ReviewAiReviewTypeLabelLocale = 'ru',
): string | null => {
    const reviewType = normalizeReviewAiReviewType(value);
    if (reviewType) {
        return getReviewAiReviewTypeLabel(reviewType, locale);
    }

    return typeof value === 'string' && value.trim() ? value.trim() : null;
};

export const buildReviewAiReviewTypeOptions = ({
    allLabel,
    allValue,
    locale = 'ru',
}: {
    allLabel: string;
    allValue: string;
    locale?: ReviewAiReviewTypeLabelLocale;
}): ReviewAiReviewTypeOption[] => [
    { value: allValue, name: allLabel },
    ...REVIEW_AI_REVIEW_TYPES.map((reviewType) => ({
        value: reviewType,
        name: getReviewAiReviewTypeLabel(reviewType, locale),
    })),
];
